import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { Message } from "@/types/index";
import { WebSocketMessage } from "@/types/websocket";
import { useUniqueMessages } from "@/hooks/useUniqueMessages";
import { logger } from "@/utils/logger";

interface UniqueMessageListProps<T extends Message | WebSocketMessage> {
  /**
   * Array de mensagens para renderizar
   */
  messages: T[];

  /**
   * Função para renderizar cada mensagem
   */
  renderMessage: (message: T, index: number) => React.ReactNode;

  /**
   * Componente a ser renderizado quando não há mensagens
   */
  emptyComponent?: React.ReactNode;

  /**
   * Componente de loading
   */
  loadingComponent?: React.ReactNode;

  /**
   * Estado de loading
   */
  loading?: boolean;

  /**
   * Classe CSS para o container
   */
  className?: string;

  /**
   * ID para acessibilidade
   */
  id?: string;

  /**
   * Role para acessibilidade
   */
  role?: string;

  /**
   * Configurações para remoção de duplicatas
   */
  uniqueOptions?: {
    uniqueKey?: keyof T;
    compareFn?: (message1: T, message2: T) => boolean;
    sort?: boolean;
    sortKey?: keyof T;
    sortOrder?: "asc" | "desc";
  };

  /**
   * Callback executado quando a lista é atualizada
   */
  onMessagesUpdate?: (uniqueMessages: T[]) => void;

  /**
   * Callback executado quando há duplicatas removidas
   */
  onDuplicatesRemoved?: (removedCount: number) => void;
}

export interface UniqueMessageListRef {
  /**
   * Retorna as mensagens únicas atuais
   */
  getUniqueMessages: () => (Message | WebSocketMessage)[];

  /**
   * Força re-renderização da lista
   */
  refresh: () => void;
}

/**
 * Componente para renderizar lista de mensagens únicas
 *
 * @example
 * ```typescript
 * <UniqueMessageList
 *   messages={chatMessages}
 *   renderMessage={(message) => <MessageBubble message={message} />}
 *   emptyComponent={<EmptyState />}
 *   loading={loading}
 *   onDuplicatesRemoved={(count) => console.log(`Removidas ${count} duplicatas`)}
 * />
 * ```
 */
export const UniqueMessageList = forwardRef<
  UniqueMessageListRef,
  UniqueMessageListProps<Message | WebSocketMessage>
>(
  (
    {
      messages,
      renderMessage,
      emptyComponent,
      loadingComponent,
      loading = false,
      className = "",
      id,
      role = "log",
      uniqueOptions = {},
      onMessagesUpdate,
      onDuplicatesRemoved,
    },
    ref,
  ) => {
    // Usa o hook para obter mensagens únicas
    const uniqueMessages = useUniqueMessages(messages, uniqueOptions);

    // Effect para processar mensagens únicas
    useEffect(() => {
      if (messages && messages.length > 0) {
        onMessagesUpdate?.(uniqueMessages);
        
        // Notificar sobre duplicatas removidas
        const removedCount = messages.length - uniqueMessages.length;
        if (removedCount > 0) {
          onDuplicatesRemoved?.(removedCount);
        }
      } else {
        onMessagesUpdate?.([]);
      }
    }, [messages, uniqueMessages, onMessagesUpdate, onDuplicatesRemoved]);

    // Expõe métodos via ref
    useImperativeHandle(
      ref,
      () => ({
        getUniqueMessages: () => uniqueMessages,
        refresh: () => {
          // Força re-renderização
        },
      }),
      [uniqueMessages],
    );

    // Renderiza loading
    if (loading && loadingComponent) {
      return <>{loadingComponent}</>;
    }

    // Renderiza estado vazio
    if (!loading && uniqueMessages.length === 0 && emptyComponent) {
      return <>{emptyComponent}</>;
    }

    return (
      <div
        id={id}
        className={className}
        role={role}
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Lista de mensagens do chat"
      >
        {uniqueMessages.map((message, index) => (
          <React.Fragment key={`${message.id || "temp"}-${index}`}>
            {renderMessage(message, index)}
          </React.Fragment>
        ))}
      </div>
    );
  },
);

UniqueMessageList.displayName = "UniqueMessageList";

/**
 * Componente especializado para mensagens de chat
 */
export const UniqueChatMessageList = forwardRef<
  UniqueMessageListRef,
  Omit<UniqueMessageListProps<Message>, "uniqueOptions">
>((props, ref) => {
  return (
    <UniqueMessageList
      {...props}
      ref={ref}
      uniqueOptions={{
        uniqueKey: "id",
        sort: true,
        sortKey: "timestamp",
        sortOrder: "asc",
      }}
    />
  );
});

UniqueChatMessageList.displayName = "UniqueChatMessageList";

/**
 * Componente especializado para mensagens WebSocket
 */
export const UniqueWebSocketMessageList = forwardRef<
  UniqueMessageListRef,
  Omit<UniqueMessageListProps<WebSocketMessage>, "uniqueOptions">
>((props, ref) => {
  return (
    <UniqueMessageList
      {...props}
      ref={ref}
      uniqueOptions={{
        uniqueKey: "id",
        sort: true,
        sortKey: "timestamp",
        sortOrder: "asc",
      }}
    />
  );
});

UniqueWebSocketMessageList.displayName = "UniqueWebSocketMessageList";
